import { motion } from 'framer-motion'
import { Button } from '../../components/ui/button'
import Container from '../../components/ui/container'
import { ArrowRight, Globe, Users, Clock } from 'lucide-react'

export default function Hero() {
    const fadeInUp = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 },
    }

    return (
        <section className="py-12 md:py-16 bg-gradient-to-b from-primary/10 via-primary/5 to-transparent overflow-hidden">
            <Container>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        className="relative z-10"
                    >
                        <motion.div
                            {...fadeInUp}
                            className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-6"
                        >
                            <span className="text-sm font-medium text-primary">
                                Top-Tier Global Remote Talent
                            </span>
                        </motion.div>

                        <motion.h1
                            {...fadeInUp}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-5xl md:text-6xl font-bold leading-tight text-black relative"
                        >
                            Scale Your Business, With Global Talent.{' '}
                            <div className="absolute inset-0 animate-shine" />
                        </motion.h1>

                        <motion.p
                            {...fadeInUp}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mt-6 text-lg text-black/80"
                        >
                            Hire pre-vetted global professionals to automate
                            workflows, scale your business, and cut
                            costs—effortlessly.
                        </motion.p>

                        <motion.div
                            className="mt-8 flex flex-wrap gap-4"
                            {...fadeInUp}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <Button
                                size="lg"
                                className="group"
                                onClick={(e) => {
                                    e.preventDefault() // Prevent default jump behavior
                                    document
                                        .getElementById('forms')
                                        ?.scrollIntoView({ behavior: 'smooth' })
                                }}
                            >
                                <span>Get a Free Demo</span>
                                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={(e) => {
                                    e.preventDefault() // Prevent default jump behavior
                                    document
                                        .getElementById('services')
                                        ?.scrollIntoView({ behavior: 'smooth' })
                                }}
                            >
                                Explore Services
                            </Button>
                        </motion.div>

                        <motion.div
                            className="mt-12 flex gap-8"
                            {...fadeInUp}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            {[
                                { icon: Globe, text: 'Global Network' },
                                { icon: Users, text: '6k+ Talents' },
                                { icon: Clock, text: '24/7 Support' },
                            ].map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-2"
                                >
                                    <item.icon className="h-5 w-5 text-primary" />
                                    <span className="text-sm font-medium">
                                        {item.text}
                                    </span>
                                </div>
                            ))}
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="relative hidden lg:block"
                    >
                        <div className="relative w-full h-[500px]">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-80 h-80 rounded-full bg-gradient-to-r from-primary/20 to-primary/5 animate-pulse" />
                                <div className="absolute w-96 h-96 rounded-full border border-primary/20 animate-spin-slow" />
                            </div>

                            {[
                                {
                                    src: 'https://i.pravatar.cc/150?img=12',
                                    alt: 'Smiling Black male professional',
                                },
                                {
                                    src: 'https://randomuser.me/api/portraits/women/65.jpg',
                                    alt: 'Smiling Hispanic female professional',
                                },
                                {
                                    src: 'https://randomuser.me/api/portraits/men/83.jpg',
                                    alt: 'Smiling White male professional',
                                },
                                {
                                    src: 'https://randomuser.me/api/portraits/women/28.jpg',
                                    alt: 'Smiling Black female professional',
                                },
                                {
                                    src: 'https://randomuser.me/api/portraits/men/91.jpg',
                                    alt: 'Smiling Hispanic male professional',
                                },
                                {
                                    src: 'https://randomuser.me/api/portraits/women/42.jpg',
                                    alt: 'Smiling White female professional',
                                },
                            ].map((person, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.2 * i }}
                                    className={`absolute w-16 h-16 ${getPosition(
                                        i
                                    )}`}
                                >
                                    <img
                                        src={person.src}
                                        alt={person.alt}
                                        className="w-full h-full rounded-full object-cover border-4 border-background shadow-lg"
                                    />
                                </motion.div>
                            ))}

                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-24 h-24 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">
                                    PT
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </Container>
        </section>
    )
}

function getPosition(index: number) {
    const positions = [
        'top-8 left-1/2 -translate-x-1/2',
        'top-1/2 -translate-y-1/2 right-8',
        'right-8 top-1/2 -translate-y-1/2',
        'bottom-8 left-1/2 -translate-x-1/2',
        'left-8 top-1/2 -translate-y-1/2',
        'left-8 top-1/2 -translate-y-1/2',
    ]
    return positions[index]
}
